import { useRef, useSyncExternalStore } from "react";
import { produce, Draft } from "immer";

export type SetState<T> = (
  partial: T | Partial<T> | ((state: T) => void | Partial<T>),
  replace?: boolean
) => void;
export type GetState<T> = () => T;
export type Subscribe = (listener: () => void) => () => void;

export type StoreApi<T> = {
  getState: GetState<T>;
  setState: SetState<T>;
  subscribe: Subscribe;
};

export type StateCreator<T, _Middleware = unknown, _Custom = unknown, Slice = T> = (
  set: SetState<T>,
  get: GetState<T>,
  api: StoreApi<T>
) => Slice;

export type UseStore<T> = {
  <U>(selector?: (state: T) => U, equality?: (a: U, b: U) => boolean): U;
  getState: GetState<T>;
  setState: SetState<T>;
  subscribe: Subscribe;
};

const default_selector = <T,>(state: T): T => state;

export function createStore<T>(create_state: StateCreator<T>): UseStore<T> {
  let state: T;
  const listeners = new Set<() => void>();

  const api: StoreApi<T> = {
    getState: () => state,
    setState: (partial, replace = false) => {
      const next_state = typeof partial === "function"
        ? produce(state, (draft: Draft<T>) => {
            const result = (partial as (draft_state: T) => void | Partial<T>)(draft as unknown as T);
            if (result !== undefined) {
              return result as Draft<T>;
            }
            return undefined;
          }) as T
        : (replace ? (partial as T) : { ...state, ...(partial as Partial<T>) });

      if (Object.is(next_state, state)) {
        return;
      }

      state = next_state as T;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  state = create_state(api.setState, api.getState, api);

  const use_store = (<U>(selector: (state: T) => U = default_selector as (state: T) => U, equality: (a: U, b: U) => boolean = Object.is) => {
    const last_selection_ref = useRef<U>();
    const has_selection_ref = useRef(false);

    return useSyncExternalStore(
      api.subscribe,
      () => {
        const next = selector(api.getState());
        if (has_selection_ref.current && equality(last_selection_ref.current as U, next)) {
          return last_selection_ref.current as U;
        }
        has_selection_ref.current = true;
        last_selection_ref.current = next;
        return next;
      },
      () => selector(api.getState())
    );
  }) as UseStore<T>;

  use_store.getState = api.getState;
  use_store.setState = api.setState;
  use_store.subscribe = api.subscribe;

  return use_store;
}
